import React, { useState } from "react";
import {
  Button,
  Table,
  Drawer,
  Form,
  Select,
  InputNumber,
  message,
  Tag,
} from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { useQuery } from "@tanstack/react-query";
import {
  FetchProductOffers_api,
  AddProductOffer_api,
  FetchInquiryList_api,
  FetchActiveStock_api,
} from "../../Api/ProductOffers/product_offers_api";
import { useAuthChecker } from "../../Context";

export const OfferForInquiry = () => {
  const { user } = useAuthChecker();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedInquiry, setSelectedInquiry] = useState<any>(null);
  const [selectedStock, setSelectedStock] = useState<any>(null);
  const [tempQty, setTempQty] = useState(1);
  const [tempItems, setTempItems] = useState<any[]>([]);
  const [saveOfferBtnLoading, setSaveOfferBtnLoading] = useState(false);

  const [form] = Form.useForm();

  // Fetch product offers
  const {
    data: offersData,
    refetch: refetchOffers,
    isFetching: offersLoading,
  } = useQuery({
    queryKey: ["product_offers"],
    queryFn: FetchProductOffers_api,
  });

  // Fetch inquiry list
  const { data: inquiriesData } = useQuery({
    queryKey: ["inquiry_list"],
    queryFn: FetchInquiryList_api,
  });

  // Fetch active stock
  const { data: stockData } = useQuery({
    queryKey: ["active_stock"],
    queryFn: FetchActiveStock_api,
  });

  const handleAddToTemp = () => {
    if (!selectedStock) {
      message.warning("Please select a product");
      return;
    }

    if (!tempQty || tempQty < 1) {
      message.warning("Please enter a valid quantity");
      return;
    }

    const existingQty = tempItems.reduce((total, item) => {
      return item.product_has_stock_id === selectedStock.id
        ? total + item.qty
        : total;
    }, 0);

    if (tempQty + existingQty > selectedStock.qty) {
      message.error(
        `Cannot add quantity! Available: ${selectedStock.qty}, Already Added: ${existingQty}`,
      );
      return;
    }

    const newItem = {
      product_has_stock_id: selectedStock.id,
      product_name: selectedStock.product_name,
      qty: tempQty,
      cost: selectedStock.cost,
      total: tempQty * parseFloat(selectedStock.cost),
    };

    setTempItems([...tempItems, newItem]);
    setSelectedStock(null);
    setTempQty(1);
  };

  const handleRemoveItem = (index: number) => {
    const updated = tempItems.filter((_, i) => i !== index);
    setTempItems(updated);
  };

  const handleSaveOffer = async () => {
    setSaveOfferBtnLoading(true);
    try {
      if (!selectedInquiry) {
        message.error("Please select an inquiry");
        return;
      }

      if (tempItems.length === 0) {
        message.error("Please add at least one product");
        return;
      }

      const payload = {
        inquiry_id: selectedInquiry,
        items: tempItems,
        userId: user?.userId,
      };

      const res = await AddProductOffer_api(payload);
      if (res.isDataUpdated) {
        message.success(res.msg);
        setDrawerOpen(false);
        setSelectedInquiry(null);
        setTempItems([]);
        form.resetFields();
        refetchOffers();
      } else {
        message.error(res.msg);
      }
    } catch (error) {
      console.log(error);
    } finally {
      setSaveOfferBtnLoading(false);
    }
  };

  const tempItemsColumns = [
    {
      title: "Product",
      dataIndex: "product_name",
      key: "product_name",
    },
    {
      title: "Quantity",
      dataIndex: "qty",
      key: "qty",
    },
    {
      title: "Cost (Rs.)",
      dataIndex: "cost",
      key: "cost",
      render: (cost: string) => parseFloat(cost).toLocaleString(),
    },
    {
      title: "Total (Rs.)",
      dataIndex: "total",
      key: "total",
      render: (total: number) => total.toLocaleString(),
    },
    {
      title: "Action",
      key: "action",
      render: (_: any, __: any, index: number) => (
        <Button
          danger
          size="small"
          icon={<DeleteOutlined />}
          onClick={() => handleRemoveItem(index)}
        >
          Remove
        </Button>
      ),
    },
  ];

  const offerItemsColumns = [
    {
      title: "Product",
      dataIndex: "product_name",
      key: "product_name",
    },
    {
      title: "Quantity",
      dataIndex: "qty",
      key: "qty",
    },
    {
      title: "Cost (Rs.)",
      dataIndex: "cost",
      key: "cost",
      render: (cost: string) => parseFloat(cost).toLocaleString(),
    },
    {
      title: "Offer Date",
      dataIndex: "offer_date",
      key: "offer_date",
    },
  ];

  const mainColumns = [
    {
      title: "Inquiry Number",
      dataIndex: "inquiry_number",
      key: "inquiry_number",
      render: (num: string) => <span className="font-semibold">{num}</span>,
    },
    {
      title: "Customer",
      dataIndex: "customer_name",
      key: "customer_name",
    },
    {
      title: "Vehicle",
      key: "vehicle",
      render: (_: any, record: any) => (
        <div>
          <div>
            {record.vehicle_type} - {record.vehicle_modal}
          </div>
          {record.engine_number && (
            <div className="text-xs text-gray-500">
              Engine: {record.engine_number}
            </div>
          )}
        </div>
      ),
    },
    {
      title: "Total Items",
      key: "items_count",
      render: (_: any, record: any) => (
        <Tag color="blue">{record.items?.length || 0} items</Tag>
      ),
    },
    {
      title: "Total Cost",
      key: "total_cost",
      render: (_: any, record: any) => (
        <span className="font-semibold text-green-600">
          Rs. {parseFloat(record.total_cost || 0).toLocaleString()}
        </span>
      ),
    },
  ];

  const totalOfferValue = tempItems.reduce((sum, item) => sum + item.total, 0);

  return (
    <div className="w-full min-h-screen bg-gray-50 p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Offer For Inquiry</h1>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setDrawerOpen(true)}
        >
          Add Offer
        </Button>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <Table
          columns={mainColumns}
          dataSource={offersData?.offers || []}
          rowKey="inquiry_id"
          className="overflow-x-auto"
          loading={offersLoading}
          pagination={false}
          expandable={{
            expandedRowRender: (record) => (
              <div className="p-4 bg-gray-50">
                <h3 className="text-sm font-semibold mb-3">Offered Products</h3>
                {record.items && record.items.length > 0 ? (
                  <Table
                    className="overflow-x-auto"
                    columns={offerItemsColumns}
                    dataSource={record.items}
                    rowKey="offer_id"
                    pagination={false}
                    size="small"
                  />
                ) : (
                  <p className="text-gray-500 text-sm">No items offered</p>
                )}
              </div>
            ),
            rowExpandable: (record) => record.items && record.items.length > 0,
          }}
        />
      </div>

      {/* Add Offer Drawer */}
      <Drawer
        title="Add Product Offer"
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedInquiry(null);
          setTempItems([]);
          form.resetFields();
        }}
        width={720}
        footer={
          <div className="flex justify-end gap-2">
            <Button
              onClick={() => {
                setDrawerOpen(false);
                setSelectedInquiry(null);
                setTempItems([]);
                form.resetFields();
              }}
            >
              Cancel
            </Button>
            <Button
              loading={saveOfferBtnLoading}
              type="primary"
              onClick={handleSaveOffer}
            >
              Save Offer
            </Button>
          </div>
        }
      >
        <Form form={form} layout="vertical">
          <Form.Item label="Select Inquiry" required>
            <Select
              placeholder="Select inquiry"
              value={selectedInquiry}
              onChange={(value) => setSelectedInquiry(value)}
              showSearch
              optionFilterProp="children"
            >
              {inquiriesData?.inquiries?.map((inq: any) => (
                <Select.Option key={inq.id} value={inq.id}>
                  {inq.inquiryNumber} - {inq.customer_name} ({inq.v_type}
                  {" - "}
                  {inq.v_modal})
                </Select.Option>
              ))}
            </Select>
          </Form.Item>

          <div className="border-t pt-4 mt-4">
            <h3 className="font-semibold mb-3">Add Products</h3>
            <div className="flex gap-2 mb-4">
              <Select
                showSearch
                placeholder="Select product"
                value={selectedStock?.id}
                onChange={(value) => {
                  const stock = stockData?.stock?.find(
                    (s: any) => s.id === value,
                  );
                  setSelectedStock(stock);
                }}
                className="flex-1"
              >
                {stockData?.stock?.map((s: any) => (
                  <Select.Option key={s.id} value={s.id}>
                    {s.product_name} - Rs. {parseFloat(s.cost).toLocaleString()}{" "}
                    (Qty: {s.qty})
                  </Select.Option>
                ))}
              </Select>
              <InputNumber
                min={1}
                max={selectedStock?.qty || 999}
                value={tempQty}
                onChange={(val) => setTempQty(val || 1)}
                placeholder="Qty"
                style={{ width: 100 }}
              />
              <Button type="dashed" onClick={handleAddToTemp}>
                Add
              </Button>
            </div>

            {tempItems.length > 0 && (
              <>
                <Table
                  columns={tempItemsColumns}
                  dataSource={tempItems}
                  rowKey={(_, index) => index?.toString() || "0"}
                  pagination={false}
                  size="small"
                  className="mb-4 overflow-x-auto"
                />
                <div className="bg-blue-50 p-3 rounded">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total Offer Value:</span>
                    <span className="text-lg font-bold text-blue-600">
                      Rs. {totalOfferValue.toLocaleString()}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </Form>
      </Drawer>
    </div>
  );
};
